import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ImagePlus,
  Loader2,
  Package,
  Sparkles,
  Tag,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { categories as categoriesApi, products } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { convertEnteredPriceToUsd, pickCategoryIdFromHints } from '@/lib/sellerPricing';
import { cn, generateSlug } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const STAGES = [
  'Moondream görseli inceliyor (ilk sefer ~2 dk sürebilir)...',
  'Ürün tipi okunuyor...',
  'Forma aktarılıyor...',
];

const STEPS = [
  { id: 1, label: 'Görsel yükle' },
  { id: 2, label: 'AI analizi' },
  { id: 3, label: 'Düzenle & kaydet' },
];

function errorText(err) {
  if (typeof err?.clientMessage === 'string' && err.clientMessage.trim()) return err.clientMessage;
  if (err?.response?.status === 504) return 'Analiz zaman aşımına uğradı. Tekrar dene.';
  const data = err?.response?.data;
  const detail = data?.message || data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter((item) => typeof item === 'string' && item.trim())
      .join(' ');
    if (text) return text;
  }
  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
  return 'AI servisi şu anda kullanılamıyor.';
}

function draftFromAnalysis(data, categoryOptions) {
  const description = data.description || data.short_description || '';
  return {
    product_name: data.product_name || '',
    description,
    category_id: pickCategoryIdFromHints(data, categoryOptions),
    brand: data.brand || '',
    gender: data.gender || '',
    color: data.color || '',
    material: data.material || '',
    sizes: (data.sizes || []).join(', '),
    seo_title: data.seo_title || data.product_name || '',
    seo_description: data.seo_description || data.short_description || '',
    keywords: (data.keywords || data.tags || []).join(', '),
    price: '',
    stock: '',
    warnings: (Array.isArray(data.warnings) ? data.warnings : []).filter((item) => typeof item === 'string'),
    vision_insufficient: Boolean(data.vision_insufficient),
    extra: data,
  };
}

function StepIndicator({ activeStep }) {
  return (
    <ol className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {STEPS.map((step, index) => {
        const done = activeStep > step.id;
        const current = activeStep === step.id;
        return (
          <li key={step.id} className="flex flex-1 items-center gap-3 min-w-0">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                done && 'border-emerald-500 bg-emerald-500 text-white',
                current && !done && 'border-red-500 bg-red-50 text-red-600',
                !done && !current && 'border-neutral-200 bg-white text-neutral-400',
              )}
            >
              {done ? <CheckCircle2 className="h-5 w-5" /> : step.id}
            </div>
            <div className="min-w-0">
              <p className={cn('text-sm font-medium truncate', current ? 'text-neutral-900' : 'text-neutral-500')}>
                {step.label}
              </p>
            </div>
            {index < STEPS.length - 1 ? (
              <div className="hidden sm:block h-px flex-1 bg-neutral-200 mx-2" aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function SectionTitle({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      {Icon ? (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
          <Icon className="h-4 w-4" />
        </div>
      ) : null}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        {description ? <p className="text-xs text-muted-foreground mt-0.5">{description}</p> : null}
      </div>
    </div>
  );
}

function Panel({ onSaved }) {
  const { currency, exchangeRates, currencySymbols } = useCurrency();
  const inputRef = useRef(null);
  const analyzeRunRef = useRef(0);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [stage, setStage] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [visionUsable, setVisionUsable] = useState(null);
  const [visionHint, setVisionHint] = useState('');

  const activeStep = draft ? 3 : analyzing ? 2 : files.length ? 2 : 1;

  useEffect(() => {
    api
      .get('/ai/products/vision-status', { timeout: 15000 })
      .then((res) => {
        const row = res.data || {};
        setVisionUsable(Boolean(row.vision_usable));
        if (!row.feature_enabled) {
          setVisionHint('AI ile ürün yükleme yönetici tarafından kapatılmış. Normal ürün ekleme kullanın.');
        } else if (!row.ollama) {
          setVisionHint('Sunucuda Ollama yok. Görsel AI çalışmaz; ürünü elle girin veya özelliği kapatın (AI_VISION_ENABLED=0).');
        } else if (!row.model_ready) {
          setVisionHint(`Moondream (${row.vision_model || 'moondream'}) yüklü değil. ollama pull moondream gerekir.`);
        } else {
          setVisionHint('');
          api.post('/ai/products/warm-vision', null, { timeout: 10000 }).catch(() => {});
        }
      })
      .catch(() => {
        setVisionUsable(false);
        setVisionHint('Görsel AI durumu okunamadı. Analiz denemesi başarısız olabilir.');
      });
  }, []);

  useEffect(() => {
    categoriesApi.list().then((res) => {
      const rows = Array.isArray(res.data) ? res.data : res.data?.categories || [];
      setCategoryOptions(rows);
    }).catch(() => setCategoryOptions([]));
  }, []);

  useEffect(() => {
    if (!draft?.extra || draft.category_id || !categoryOptions.length) return;
    const resolved = pickCategoryIdFromHints(draft.extra, categoryOptions);
    if (resolved) {
      setDraft((prev) => (prev ? { ...prev, category_id: resolved } : prev));
    }
  }, [categoryOptions, draft?.category_id, draft?.extra]);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const categoryName = useMemo(() => {
    const row = categoryOptions.find((item) => item.id === draft?.category_id);
    if (row) {
      if (row.parent_id) {
        const parent = categoryOptions.find((item) => item.id === row.parent_id);
        return parent ? `${parent.name} › ${row.name}` : row.name;
      }
      return row.name;
    }
    const extra = draft?.extra;
    return extra?.subcategory || extra?.category || extra?.estimated_product_type || '';
  }, [categoryOptions, draft]);

  const priceUsdPreview = useMemo(() => {
    if (!draft?.price || currency === 'USD') return null;
    const usd = convertEnteredPriceToUsd(draft.price, currency, exchangeRates);
    if (!usd) return null;
    return usd.toFixed(2);
  }, [draft?.price, currency, exchangeRates]);

  const takeFiles = (list) => {
    const next = [];
    for (const file of Array.from(list || [])) {
      if (!ACCEPT.includes(file.type)) {
        setError('Sadece JPG, PNG veya WEBP yüklenir.');
        return;
      }
      if (file.size > MAX_BYTES) {
        setError('Dosya boyutu 5MB altında olmalı.');
        return;
      }
      next.push(file);
      if (next.length >= 6) break;
    }
    if (!next.length) return;
    analyzeRunRef.current += 1;
    setError('');
    setDraft(null);
    setFiles(next);
  };

  const clearFiles = () => {
    analyzeRunRef.current += 1;
    setFiles([]);
    setDraft(null);
    setError('');
  };

  const waitForAnalysis = async (jobId) => {
    const started = Date.now();
    while (Date.now() - started < 660000) {
      const res = await api.get(`/ai/products/analyze/${encodeURIComponent(jobId)}`, { timeout: 30000 });
      const payload = res.data || {};
      if (!payload.pending) return payload;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    const err = new Error('timeout');
    err.clientMessage = 'Analiz çok uzun sürdü. Tekrar dene.';
    throw err;
  };

  const analyze = async () => {
    if (!files.length) return;
    const runId = ++analyzeRunRef.current;
    setAnalyzing(true);
    setError('');
    setStage(STAGES[0]);
    const timer = setInterval(() => {
      setStage((current) => STAGES[(STAGES.indexOf(current) + 1) % STAGES.length]);
    }, 4000);
    try {
      const body = new FormData();
      files.forEach((file) => body.append('images', file));
      const res = await api.post('/ai/products/analyze', body, { timeout: 60000 });
      let payload = res.data || {};
      if (payload.pending && payload.job_id) {
        payload = await waitForAnalysis(payload.job_id);
      }
      if (runId !== analyzeRunRef.current) return;
      if (!payload.success) {
        setError(payload.message || 'Ürün görseli analiz edilemedi.');
        return;
      }
      setDraft(draftFromAnalysis(payload.data || {}, categoryOptions));
    } catch (err) {
      if (runId === analyzeRunRef.current) setError(errorText(err));
    } finally {
      if (runId === analyzeRunRef.current) {
        clearInterval(timer);
        setAnalyzing(false);
        setStage('');
      }
    }
  };

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!draft?.product_name || !draft.description || !draft.category_id) {
      setError('Ürün adı, açıklama ve kategori gerekli.');
      return;
    }
    const enteredPrice = Number(draft.price);
    const stock = Number(draft.stock);
    if (!Number.isFinite(enteredPrice) || enteredPrice <= 0 || !Number.isFinite(stock) || stock < 0) {
      setError('Fiyat ve stok alanlarını sen girmelisin.');
      return;
    }
    const priceInUSD = convertEnteredPriceToUsd(enteredPrice, currency, exchangeRates);
    if (!priceInUSD) {
      setError('Fiyat dönüştürülemedi. Para birimini kontrol et.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const urls = [];
      for (const file of files) {
        const body = new FormData();
        body.append('file', file);
        const uploaded = await api.post('/auth/upload-product-image', body);
        if (!uploaded.data?.url) throw new Error('gorsel');
        urls.push(uploaded.data.url);
      }
      const extra = draft.extra || {};
      await products.create({
        name: draft.product_name.trim(),
        slug: generateSlug(draft.product_name),
        description: draft.description.trim(),
        price: priceInUSD,
        original_price: enteredPrice,
        original_currency: currency,
        category_id: draft.category_id,
        stock: Math.round(stock),
        brand: draft.brand.trim() || null,
        images: urls,
        sizes: draft.sizes.split(',').map((item) => item.trim()).filter(Boolean),
        colors: draft.color ? [draft.color] : [],
        seo_title: draft.seo_title || draft.product_name,
        seo_description: draft.seo_description || draft.description.slice(0, 160),
        original_language: 'tr',
        specifications: {
          gender: draft.gender || '',
          material: draft.material || '',
          pattern: extra.pattern || '',
          style: extra.style || '',
          season: extra.season || '',
          shoe_type: extra.shoe_type || '',
          clothing_type: extra.clothing_type || '',
          keywords: draft.keywords || '',
        },
      });
      toast.success('Ürün kaydedildi');
      setFiles([]);
      setDraft(null);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ürün kaydedilemedi. Normal ürün ekleme duruyor, bilgileri kontrol et.');
    } finally {
      setSaving(false);
    }
  };

  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30';

  return (
    <div className="space-y-6 font-[Manrope,sans-serif] [&_h1]:font-[Manrope,sans-serif]" data-testid="ai-product-upload">
      <StepIndicator activeStep={activeStep} />

      <Card className="overflow-hidden border-neutral-200/80 shadow-sm">
        <CardHeader className="pb-3 bg-gradient-to-r from-neutral-50 to-white">
          <SectionTitle
            icon={Upload}
            title="Ürün görselleri"
            description="JPG, PNG veya WEBP · En fazla 6 dosya · Dosya başına 5MB"
          />
        </CardHeader>
        <CardContent className="space-y-4 pt-2">
          <div
            className={cn(
              'relative rounded-xl border-2 border-dashed px-4 py-10 text-center transition-all',
              dragOver ? 'border-red-400 bg-red-50/50 scale-[1.01]' : 'border-neutral-200 bg-neutral-50/80 hover:border-neutral-300 hover:bg-neutral-50',
            )}
            onDragOver={(event) => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOver(false);
              takeFiles(event.dataTransfer.files);
            }}
          >
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-neutral-200/80">
              <ImagePlus className="h-7 w-7 text-red-500" />
            </div>
            <p className="text-base font-semibold text-neutral-900">Görselleri sürükle bırak</p>
            <p className="mt-1 text-sm text-muted-foreground">veya bilgisayarından seç</p>
            <Button
              type="button"
              variant="outline"
              className="mt-4 rounded-full border-neutral-300"
              onClick={() => inputRef.current?.click()}
            >
              Görsel seç
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              data-testid="ai-product-file"
              onChange={(event) => takeFiles(event.target.files)}
            />
          </div>

          {previews.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              {previews.map((src, index) => (
                <div key={src} className="group relative">
                  <img
                    src={src}
                    alt=""
                    className="h-24 w-24 rounded-xl object-cover bg-neutral-100 ring-1 ring-neutral-200 shadow-sm"
                  />
                  {index === 0 ? (
                    <span className="absolute left-1 top-1 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                      Kapak
                    </span>
                  ) : null}
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={clearFiles}>
                <X className="mr-1 h-4 w-4" />
                Temizle
              </Button>
            </div>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-1">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                className="rounded-full bg-red-600 hover:bg-red-700 shadow-sm"
                onClick={analyze}
                disabled={!files.length || analyzing || saving || visionUsable === false}
                data-testid="ai-product-analyze"
              >
                {analyzing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {analyzing ? stage : 'AI ile analiz et'}
              </Button>
              {draft ? (
                <Button type="button" variant="outline" className="rounded-full" onClick={analyze} disabled={analyzing}>
                  Tekrar analiz et
                </Button>
              ) : null}
            </div>
            {analyzing ? (
              <p className="text-xs text-muted-foreground animate-pulse">Analiz sürüyor, sayfayı kapatma.</p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {visionUsable === false && visionHint ? (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{visionHint}</p>
        </div>
      ) : null}

      {error ? (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{error}</p>
        </div>
      ) : null}

      {draft ? (
        <Card className="border-neutral-200/80 shadow-sm">
          <CardHeader className="pb-4">
            <SectionTitle
              icon={Package}
              title="Ürün taslağı"
              description={
                draft.vision_insufficient
                  ? 'AI ürünü tanıyamadı — ad, açıklama ve kategoriyi sen doldur; renk yalnızca ipucu.'
                  : 'AI önerilerini kontrol et, fiyat ve stok gir, kaydet.'
              }
            />
          </CardHeader>
          <CardContent className="space-y-6">
            {draft.warnings.length > 0 ? (
              <div className="rounded-xl border border-amber-200/80 bg-amber-50/90 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80 mb-2">Notlar</p>
                <ul className="space-y-1.5">
                  {draft.warnings.map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-amber-900">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      <span>{String(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="space-y-4">
              <SectionTitle icon={Tag} title="Temel bilgiler" />
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ai-product-name">Ürün adı</Label>
                  <Input
                    id="ai-product-name"
                    className="bg-white"
                    placeholder={draft.vision_insufficient ? 'Örn. Kırmızı kablosuz matkap' : 'Ürün adı'}
                    value={draft.product_name}
                    onChange={(event) => setField('product_name', event.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai-product-desc">Açıklama</Label>
                  <Textarea
                    id="ai-product-desc"
                    className="min-h-[100px] bg-white"
                    value={draft.description}
                    onChange={(event) => setField('description', event.target.value)}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="ai-product-category">Kategori</Label>
                    <select
                      id="ai-product-category"
                      className={selectClass}
                      value={draft.category_id}
                      onChange={(event) => setField('category_id', event.target.value)}
                    >
                      <option value="">{categoryName || 'Kategori seç'}</option>
                      {categoryOptions.map((item) => {
                        const parent = item.parent_id
                          ? categoryOptions.find((row) => row.id === item.parent_id)
                          : null;
                        const label = parent ? `${parent.name} › ${item.name}` : item.name;
                        return (
                          <option key={item.id} value={item.id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ai-product-brand">Marka</Label>
                    <Input
                      id="ai-product-brand"
                      value={draft.brand}
                      onChange={(event) => setField('brand', event.target.value)}
                      placeholder="Görselden emin değilse boş bırak"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <SectionTitle title="Özellikler" description="Renk, beden ve materyal" />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label>Cinsiyet</Label>
                  <Input value={draft.gender} onChange={(event) => setField('gender', event.target.value)} placeholder="Kadın, Erkek…" />
                </div>
                <div className="space-y-2">
                  <Label>Renk</Label>
                  <Input value={draft.color} onChange={(event) => setField('color', event.target.value)} placeholder="kırmızı" />
                </div>
                <div className="space-y-2">
                  <Label>Materyal</Label>
                  <Input value={draft.material} onChange={(event) => setField('material', event.target.value)} />
                </div>
                <div className="space-y-2 sm:col-span-2 lg:col-span-3">
                  <Label>Beden</Label>
                  <Input
                    value={draft.sizes}
                    onChange={(event) => setField('sizes', event.target.value)}
                    placeholder="S, M, L veya 38, 39, 40"
                  />
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <SectionTitle title="Satış bilgileri" description="Fiyat ve stok zorunlu" />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Stok</Label>
                  <Input
                    value={draft.stock}
                    onChange={(event) => setField('stock', event.target.value)}
                    placeholder="Örn. 25"
                    inputMode="numeric"
                    className="bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Fiyat ({currency}
                    {currencySymbols?.[currency] ? ` ${currencySymbols[currency]}` : ''})
                  </Label>
                  <Input
                    value={draft.price}
                    onChange={(event) => setField('price', event.target.value)}
                    placeholder={currency === 'TRY' ? 'Örn. 1500' : 'Örn. 29.99'}
                    inputMode="decimal"
                    className="bg-white"
                  />
                  {priceUsdPreview ? (
                    <p className="text-xs text-muted-foreground">≈ ${priceUsdPreview} USD (site kurları)</p>
                  ) : null}
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <SectionTitle title="SEO" description="Arama motorları için isteğe bağlı düzenleme" />
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>SEO başlık</Label>
                  <Input value={draft.seo_title} onChange={(event) => setField('seo_title', event.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>SEO açıklama</Label>
                  <Textarea
                    className="min-h-[80px]"
                    value={draft.seo_description}
                    onChange={(event) => setField('seo_description', event.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-2 border-t border-neutral-100">
              <Button type="button" variant="outline" className="rounded-full sm:min-w-[140px]" onClick={analyze} disabled={analyzing}>
                Tekrar analiz et
              </Button>
              <Button
                type="button"
                className="rounded-full bg-red-600 hover:bg-red-700 sm:min-w-[180px] shadow-sm"
                onClick={save}
                disabled={saving}
                data-testid="ai-product-save"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Ürünü kaydet
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export default function AIProductUpload({ embedded = false }) {
  const [open, setOpen] = useState(false);
  if (embedded) {
    return (
      <div className="p-4 sm:p-6 max-w-4xl mx-auto font-[Manrope,sans-serif] [&_h1]:font-[Manrope,sans-serif]">
        <div className="mb-8 rounded-2xl border border-red-100 bg-gradient-to-br from-red-50/80 via-white to-orange-50/50 p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white shadow-md shadow-red-200">
              <Sparkles className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-neutral-900">AI ile ürün yükle</h1>
              <p className="mt-1 text-sm text-muted-foreground max-w-xl">
                Ürün fotoğrafını yükle; sistem ad, açıklama, renk ve kategori önerir. Fiyat ve stoku sen girersin.
              </p>
            </div>
          </div>
        </div>
        <Panel />
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="ai-product-open">
        <Sparkles className="w-4 h-4 mr-2" />
        AI ile Ürün Yükle
      </Button>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-2 border-b bg-neutral-50/80">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-red-600" />
            AI ile ürün yükle
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-6">
          <Panel onSaved={() => setOpen(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
